import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowRight,
  CheckCircle,
  Cpu,
  FileText,
  Link2,
  Lightbulb,
  Send,
  Loader2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const partnerTypes = [
  {
    icon: Cpu,
    title: 'Technology Partners',
    description: 'Integrate your technology with SusTek platform.',
    examples: 'IoT providers, data platforms, ERP systems, BMS providers',
  },
  {
    icon: FileText,
    title: 'Standards Bodies',
    description: 'Collaborate on methodology development and standard adoption.',
    examples: 'Sustainability frameworks, verification bodies, registries',
  },
  {
    icon: Link2,
    title: 'Blockchain & Registry',
    description: 'Build tokenization and registry infrastructure together.',
    examples: 'Carbon registries, blockchain platforms, marketplaces',
  },
  {
    icon: Lightbulb,
    title: 'Consulting Partners',
    description: 'Deliver sustainability transformation projects with SusTek technology.',
    examples: 'Sustainability consultancies, Big 4 firms, system integrators',
  },
];

const partnershipInterests = [
  'Technology Integration',
  'Standards & Methodology',
  'Blockchain & Registry',
  'Consulting & Implementation',
  'Co-Innovation',
  'Other',
];

export const Partners = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    partnerType: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/partner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          partner_type: formData.partnerType,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      const result = await response.json();
      
      setIsSubmitting(false);
      setIsSubmitted(true);
      toast.success('Partnership inquiry submitted successfully!');
    } catch (error) {
      console.error('Form submission error:', error);
      setIsSubmitting(false);
      toast.error('Failed to submit inquiry. Please try again.');
    }
  };

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge variant="outline" className="mb-4">Partners</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
              Partner with SusTek
            </h1>
            <p className="text-xl text-muted-foreground">
              Co-build dMRV and tokenization systems that scale. Joint solution
              development, pilot-to-scale programs, and API-first services.
            </p>
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">Partnership Types</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Ways to Partner
            </h2>
            <p className="text-lg text-muted-foreground">
              We work with organizations across the sustainability ecosystem to deliver
              comprehensive solutions.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {partnerTypes.map((type, index) => (
              <Card key={index} className="border-border/50 bg-card hover-lift">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                      <type.icon className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-xl">{type.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base mb-4">
                    {type.description}
                  </CardDescription>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium text-foreground">Examples:</span>{' '}
                    {type.examples}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge variant="outline" className="mb-4">Partner Benefits</Badge>
              <h2 className="text-3xl font-bold text-foreground mb-6">
                Why Partner with SusTek
              </h2>
              <ul className="space-y-4">
                {[
                  'Access to enterprise-grade sustainability data infrastructure',
                  'Joint go-to-market programs and co-selling opportunities',
                  'Technical integration support and API documentation',
                  'Co-innovation on cutting-edge dMRV and tokenization solutions',
                  'Thought leadership and industry visibility',
                ].map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-teal flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80"
                alt="Business Partnership"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Partner Form */}
      <section className="py-20 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge variant="outline" className="mb-4">Get Started</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Become a Partner
            </h2>
            <p className="text-lg text-muted-foreground">
              Tell us about your organization and how you would like to partner.
            </p>
          </div>

          <Card className="border-border/50 bg-card">
            <CardContent className="pt-6">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-success" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Inquiry Submitted!
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Thank you for your interest in partnering with SusTek. Our partnerships
                    team will review your inquiry and be in touch within 2 business days.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsSubmitted(false);
                      setFormData({
                        name: '',
                        email: '',
                        company: '',
                        partnerType: '',
                        message: '',
                      });
                    }}
                  >
                    Submit Another Inquiry
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company">Company Name *</Label>
                      <Input
                        id="company"
                        placeholder="Company Name"
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        required
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="partnerType">Partnership Interest *</Label>
                      <Select
                        value={formData.partnerType}
                        onValueChange={(value) => handleChange('partnerType', value)}
                        disabled={isSubmitting}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {partnershipInterests.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Tell us about your partnership interest *</Label>
                    <Textarea
                      id="message"
                      placeholder="Describe your organization and how you would like to partner with SusTek..."
                      rows={5}
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="teal"
                    size="lg"
                    className="w-full group"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Partnership Inquiry
                        <Send className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Partners;
